/**
 * Badge variant type - mirrors @bridge/ui BadgeProps['variant']
 * Defined here to avoid circular dependency with @bridge/ui
 */
export type BadgeVariant =
  | 'default'
  | 'positive'
  | 'negative'
  | 'warning'
  | 'option'
  | 'success'
  | 'danger'
  | 'pending'
  | 'review'
  | 'complete';

/**
 * Semantic badge variants for consistent status representation across the application
 */
export const BADGE_VARIANTS = {
  /** Success states, completed actions, positive outcomes */
  positive: 'positive' as const,
  /** Error states, failed actions, negative outcomes */
  negative: 'negative' as const,
  /** Pending states, warnings, cautionary items */
  warning: 'warning' as const,
  /** Default/neutral states */
  default: 'default' as const,
  /** Optional/selectable items */
  option: 'option' as const,
  /** @deprecated Use 'positive' instead */
  success: 'success' as const,
  /** @deprecated Use 'negative' instead */
  danger: 'danger' as const,
  /** @deprecated Use 'default' instead */
  pending: 'pending' as const,
  /** @deprecated Use 'warning' instead */
  review: 'review' as const,
  /** @deprecated Use 'positive' instead */
  complete: 'complete' as const,
} as const satisfies Record<string, BadgeVariant>;

/**
 * Standard column widths for common table column types
 * Use these to maintain consistent sizing across tables
 */
export const COLUMN_WIDTHS = {
  /** Action buttons column (e.g., copy, delete) */
  action: 36,
  /** Short action columns with multiple buttons */
  actionWide: 60,
  /** Status badge columns */
  status: 100,
  statusCode: 70,
  statusWide: 160,
  /** Date/timestamp columns */
  date: 160,
  /** Date columns that also show a time or relative phrasing */
  dateTime: 150,
  /** Compact date columns (no time) */
  dateShort: 90,
  /** Amount/currency columns */
  amount: 120,
  /** Short ID columns (e.g., "va_123...") */
  idShort: 120,
  /** Medium ID columns (e.g., "va_1234567890") */
  idMedium: 160,
  /** Full ID columns (e.g., "va_12345678901234567890") */
  idFull: 200,
  /** Email columns */
  email: 220,
  /** Standard text columns */
  textShort: 140,
  /** Standard text columns */
  text: 180,
  /** Wide text columns for longer content */
  textWide: 240,
  /** Checkbox columns */
  checkbox: 48,
} as const;

/**
 * Common icon sizes used throughout the application
 */
export const ICON_SIZES = {
  xs: 12,
  sm: 14,
  md: 16,
  lg: 20,
  xl: 24,
} as const;

/**
 * Standard spacing values for consistent layout
 */
export const SPACING = {
  xs: 4,
  sm: 8,
  md: 16,
  lg: 24,
  xl: 32,
  xxl: 48,
} as const;
